import PropTypes from "prop-types";
import { useMemo, useState } from "react";
// @mui
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
// components
import Iconify from "src/components/iconify";

export default function ColumnChooser({ anchorEl, columns, ctx, onClose }) {
  const [search, setSearch] = useState("");

  const items = useMemo(() => {
    const term = search.trim().toLowerCase();

    return columns
      .filter(column => column.showInColumnChooser && (column.caption || column.dataField))
      .filter(column => !term || String(column.caption).toLowerCase().includes(term))
      .map(column => ({ column, state: ctx.getColumnState(column) }))
      .sort((a, b) => a.state.order - b.state.order);
  }, [columns, ctx, search]);

  const hidden = items.filter(item => !item.state.visible && item.column.allowHiding !== false);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { width: 300, maxWidth: "calc(100vw - 32px)" } } }}
    >
      <Stack sx={{ p: 1.5, pb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1 }}
        >
          {ctx.t("gridColumnChooser")}
        </Typography>
        <InputBase
          autoFocus
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={ctx.t("gridSearch")}
          startAdornment={
            <Iconify
              icon="eva:search-fill"
              width={18}
              sx={{ color: "text.disabled", mr: 1 }}
            />
          }
          sx={{
            px: 1,
            height: 36,
            borderRadius: 1,
            bgcolor: theme => theme.palette.action.hover,
          }}
        />
      </Stack>
      <Divider />
      <Box sx={{ maxHeight: 360, overflowY: "auto", px: 1.5, py: 0.5 }}>
        {items.map(({ column, state }) => (
          <Stack
            key={column.id}
            direction="row"
            alignItems="center"
            component="label"
            sx={{ minHeight: 34, cursor: column.allowHiding ? "pointer" : "default", pl: 0.5 }}
          >
            <Checkbox
              size="small"
              checked={state.visible}
              disabled={!column.allowHiding || typeof state.groupIndex === "number"}
              onChange={event => ctx.setColumnVisible(column, event.target.checked)}
              sx={{ p: 0.5 }}
            />
            <Typography
              variant="body2"
              noWrap
              title={column.caption}
            >
              {column.caption || column.dataField}
            </Typography>
          </Stack>
        ))}
        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ py: 2, color: "text.disabled", textAlign: "center" }}
          >
            {ctx.t("gridNoData")}
          </Typography>
        ) : null}
      </Box>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ p: 1 }}
      >
        <Button
          size="small"
          color="inherit"
          disabled={hidden.length === 0}
          onClick={() => hidden.forEach(({ column }) => ctx.setColumnVisible(column, true))}
        >
          {ctx.t("gridShowAll")}
        </Button>
        <Button
          size="small"
          color="inherit"
          onClick={onClose}
        >
          {ctx.t("close")}
        </Button>
      </Stack>
    </Popover>
  );
}

ColumnChooser.propTypes = {
  anchorEl: PropTypes.any,
  columns: PropTypes.array,
  ctx: PropTypes.object,
  onClose: PropTypes.func,
};
